import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewLinkProviderCandidates from '@app/src/components/Drawer/Views/DrawerViewLinkProviderCandidates'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useProvidersModal } from '@app/src/context/ProvidersModalContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Provider, ProviderOrganizationResult } from '@app/src/types/organizations'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { Box, CircularProgress, Dialog } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import { useHistory } from 'react-router-dom'
import { ProviderTabs } from '../ProvidersScene'
import { ConnectionSummaryStep, ModalHeader, ProviderSelectionStep, StepHeading } from './components'
import type { ConnectOnlyFlowProvidersModalProps, LinkProviderRequest } from './types'

const ConnectOnlyFlowProvidersModal = ({ open, onClose }: ConnectOnlyFlowProvidersModalProps): JSX.Element => {
  const { formatMessage } = useIntl()
  const { trackEvent } = useAmplitude()
  const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([])
  const { openDrawer } = useDrawer()
  const { exactMatches = [] } = useProvidersModal()
  const [connectedCount, setConnectedCount] = useState(0)
  const [showSummary, setShowSummary] = useState(false)
  const [hasConnectedProviders, setHasConnectedProviders] = useState(false)
  const queryClient = useQueryClient()
  const history = useHistory()

  const { items: unlinkedProviders = [], isLoading: isUnlinkedProvidersLoading } = useFetchCollectionWithPost<Provider>(
    {
      key: [FetchKey.ProviderCollection],
      endpoint: endpoints.providersCollection,
      payload: {
        filter: [
          {
            name: 'linked',
            filters: [{ operator: Operators.EqualTo, value: false }],
          },
        ],
        include: ['referralContact', 'organization', 'providerOwners.user'],
      },
    },
  )

  const connectableProviders = useMemo(
    () => unlinkedProviders.filter(provider => exactMatches.some(match => match.providerId === provider.id)),
    [unlinkedProviders, exactMatches],
  )

  const { mutateAsync: connectBulkProviders, isPending: isConnectingBulk } = useUpdateResource<
    void,
    LinkProviderRequest[]
  >()
  const { showSnackbar } = useSnackbar()

  useEffect(() => {
    if (connectableProviders.length > 0) {
      const newSelectedIds = connectableProviders.map(p => p.id)
      setSelectedProviderIds(newSelectedIds)
    }
  }, [connectableProviders])

  useEffect(() => {
    if (open) {
      trackEvent({
        name: AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.ModalOpened,
        eventProps: {
          connectable_providers_count: connectableProviders.length,
          exact_matches_count: exactMatches.length,
        },
      })

      if (!hasConnectedProviders) {
        setShowSummary(false)
        setConnectedCount(0)
        if (!isUnlinkedProvidersLoading && connectableProviders.length === 0) {
          setShowSummary(true)
        }
      }
    } else {
      setHasConnectedProviders(false)
    }
  }, [
    open,
    exactMatches.length,
    trackEvent,
    hasConnectedProviders,
    isUnlinkedProvidersLoading,
    connectableProviders.length,
  ])

  const showSuccessNotification = (isBulk: boolean) => {
    showSnackbar({
      message: formatMessage({
        id: `connectOnlyFlowProvidersModal.notifications.${isBulk ? 'successfulBulkLink' : 'successfulLink'}`,
      }),
      severity: 'success',
    })
  }

  const showErrorNotification = (isBulk: boolean) => {
    showSnackbar({
      message: formatMessage({
        id: `connectOnlyFlowProvidersModal.notifications.${isBulk ? 'failedBulkLink' : 'failedLink'}`,
      }),
      severity: 'error',
    })
  }

  const handleViewProvider = (result: ProviderOrganizationResult) => {
    trackEvent({
      name: AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.ProviderViewed,
      eventProps: {
        provider_id: result.providerId,
        organizations_count: result.organizations.length,
      },
    })

    const provider = unlinkedProviders.find(p => p.id === result.providerId)
    if (provider) {
      const firstOrganization = result.organizations[0]
      if (!firstOrganization) {
        return
      }

      const organizationCandidate: Provider = {
        ...firstOrganization,
        alreadyLinkedOrganization: false,
        linked: false,
        linkStatus: 'Created' as const,
        targetOrganizationId: provider.id,
        organization: firstOrganization,
        organizationId: firstOrganization.id,
        type: provider.type,
      } as Provider

      openDrawer(
        <DrawerViewLinkProviderCandidates
          resource={provider}
          referralContact={provider.referralContact}
          organizationCandidates={[organizationCandidate]}
          showBackButton={false}
        />,
        { showAboveDialogs: true },
      )
    }
  }

  const handleConnectProviders = async (providerIds: number[]): Promise<void> => {
    try {
      const providersToConnect: LinkProviderRequest[] = connectableProviders
        .filter(provider => providerIds.includes(provider.id))
        .map(provider => {
          const matchingOrg = exactMatches.find(match => match.providerId === provider.id)
          const organizationId = matchingOrg?.organizations[0]?.id
          if (!organizationId) return null
          const request: LinkProviderRequest = {
            id: provider.id,
            organizationId,
            ...(provider.ownerUserIds && provider.ownerUserIds.length ? { ownerUserIds: provider.ownerUserIds } : {}),
          }
          return request
        })
        .filter((provider): provider is LinkProviderRequest => provider !== null)

      if (providersToConnect.length === 0) {
        showSnackbar({
          message: formatMessage({ id: 'connectOnlyFlowProvidersModal.notifications.providerNotFound' }),
          severity: 'error',
        })
        return
      }

      await connectBulkProviders({
        url: endpoints.bulkLinkOrganizationsWithProviders,
        body: providersToConnect,
      })
      await queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })

      const isBulk = providerIds.length > 1
      const newConnectedCount = providersToConnect.length

      if (isBulk) {
        setConnectedCount(newConnectedCount)
      } else {
        setConnectedCount(prev => prev + newConnectedCount)
      }

      setShowSummary(true)
      setHasConnectedProviders(true)

      showSuccessNotification(isBulk)

      trackEvent({
        name: isBulk
          ? AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.AllProvidersConnected
          : AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.ProviderConnected,
        eventProps: {
          providers_count: providersToConnect.length,
          provider_ids: providersToConnect.map(p => p.id),
          connection_method: isBulk ? 'bulk' : 'single',
          ...(providersToConnect.length === 1 && {
            provider_id: providersToConnect[0].id,
            organization_id: providersToConnect[0].organizationId,
          }),
        },
      })
    } catch (error) {
      const isBulk = providerIds.length > 1
      showErrorNotification(isBulk)
    }
  }

  const handleConnectSingle = async (providerId: number, organizationId: number): Promise<void> => {
    await handleConnectProviders([providerId])
  }

  const handleConnectAll = async () => {
    await handleConnectProviders(selectedProviderIds)
  }

  const handleSummaryClose = () => {
    trackEvent({
      name: AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.ModalClosed,
      eventProps: {
        connected_count: connectedCount,
        total_connectable_providers: connectableProviders.length,
      },
    })
    onClose()
    history.push(generatePath(paths.providers, { view: ProviderTabs.NotConnected }))
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen closeAfterTransition>
      <FormProvider {...useForm()}>
        {!showSummary && <ModalHeader titleKey="connectOnlyFlowProvidersModal.step1.heading" onClose={onClose} />}
        {showSummary ? (
          <ConnectionSummaryStep connectedCount={connectedCount} onClose={handleSummaryClose} />
        ) : (
          <Box maxWidth={800} mx="auto" px={4} py={2}>
            {isUnlinkedProvidersLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <StepHeading step={1} count={connectableProviders.length} />
                <ProviderSelectionStep
                  providers={connectableProviders}
                  selectedProviderIds={selectedProviderIds}
                  setSelectedProviderIds={setSelectedProviderIds}
                  handleConnectSingle={handleConnectSingle}
                  handleConnectAll={handleConnectAll}
                  handleViewProvider={handleViewProvider}
                  isConnectingAll={isConnectingBulk}
                  exactMatches={exactMatches}
                />
              </>
            )}
          </Box>
        )}
      </FormProvider>
    </Dialog>
  )
}

export default ConnectOnlyFlowProvidersModal
